<template>
	<div class="clips-wrapper panel--full-height">
		<FilterableList
			ref="clipList"
			:title="$I18n.trans('commons.event_plan')"
			:active-filters="activeFiltersComp"
			:hide-title-visibility="isSearchActive"
			modifier="tag-template-list"
		>
			<template #over-title>
				<div
					v-if="isSearchActive"
					class="search"
				>
					<ClipSearchInput
						:placeholder="transUcfirst('commons.search')"
						:search-keyword="searchKeyword"
						@close="closeOrClearSearch"
						@change="onSearchInput"
					/>
				</div>
			</template>
			<template #filters-dropdown>
				<SearchButton
					v-if="isSearchBtnVisible && !isSearchActive"
					@click="isSearchActive=true"
				/>
			</template>
			<template #list-view>
				<div class="list--container list--view">
					<div class="list-body">
						<div
							v-if="tagTemplatesAndClipsIsEmtpy"
							class="flex flex-1 no-data"
							:class="{ 'p-2': isMobileWebApp, 'p-5': !isMobileWebApp }"
						>
							<NoResults
								:title="emptyMsg.title"
								:description="emptyMsg.description"
							/>
						</div>
						<EventBannerAd v-if="!isSearchActive" />
						<Accordion
							ref="accordion"
							:items="tagTemplatesAndClips"
							modifier="minimal-styles"
							:keep-open="true"
							:icon-position-right="'8'"
						>
							<template #header="{item}">
								<div class="title-wrapper tag-section-title-w">
									<h2>{{ item.sectionTitle }}</h2>
								</div>
							</template>
							<template #body="{item}">
								<div class="tag-template-list__items js-scroll-wrapper">
									<div
										v-for="(subItem, i) in item.items"
										:key="i"
									>
										<TagTemplateTile
											v-if="subItem.isTagTemplate"
											:item="subItem"
										/>
										<ClipListTile
											v-else
											:clip="subItem.clip"
											:event-id="event.id"
											:is-playing="subItem.clip.id === itemPlayingId"
											:hide-share="subItem.clip.analysisOnly || !selectedTagGroupIsPublic"
											@play="handlePlay"
											@share="handleShare"
										/>
									</div>
								</div>
							</template>
						</Accordion>
					</div>
				</div>
			</template>
		</FilterableList>
		<ShareModal
			v-if="isShareModalOpen"
			:title="shareClipModalTitle"
			:event-title="eventTitle"
			:goal-score="goalScore"
			:item="shareTag"
			:item-label="transTagLabel(shareTag.name, event.sportName)"
			:url-label="urlLabel"
			:url="shareUrl"
			:links="shareLinks"
			@close="closeShareModal"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FilterableList from '../lists/FilterableList';
import { playerBus } from '@videotools/js/events';
import ShareModal from '../share-modal/ShareModal';
import EventBannerAd from '@components/EventBannerAd';
import { TAG_NAMES, TAG_TYPE_CLOCK } from '@videotools/js/api/entities/TagButton';
import ClipListTile from '@components/clip-list/ClipListTile';
import TagTemplateTile from '@components/clip-list/TagTemplateTile';
import ClipSearchInput from '@components/search/ClipSearchInput';
import SearchButton from '@components/search/SearchButton';
import NoResults from '@videotools/js/analysis/components/TagList/NoResults';
import Accordion from '@shared/components/Accordion';

export default {
	name:       'TagTemplateList',
	components: {
		NoResults,
		SearchButton,
		ClipSearchInput,
		ClipListTile,
		TagTemplateTile,
		ShareModal,
		FilterableList,
		EventBannerAd,
		Accordion,
	},
	data() {
		return {
			isShareModalOpen:   false,
			shareTag:           {},
			goalScore:          '',
			shareLinks:         null,
			shareUrl:           null,
			onModalClose:       null,
			clipsData:          [],
			itemPlayingId:      null,
			isSearchActive:     false,
			isSearchBtnVisible: true,
			accordionOpened:    false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:           'app/isMobileWebApp',
			isTouchDevice:            'device/isTouchDevice',
			supportsNativeShare:      'device/supportsNativeShare',
			eventTitle:               'event/eventTitle',
			event:                    'event/event',
			isLive:                   'event/isLive',
			sportName:                'event/sportName',
			eventTags:                'event/tags',
			activeFilters:            'clipFilters/activeFilters',
			searchKeyWordStore:       'clipFilters/searchKeyword',
			selectedTagGroupIsPublic: 'tagGroups/selectedTagGroupIsPublic',
			tagTemplates:             'event/tagTemplates',
		}),
		clips() {
			return this.clipsData;
		},
		tagTemplatesAndClips() {
			const sections = [];
			let clips = [...this.clipsData];

			// Iterate over tagTemplates
			this.tagTemplates.forEach(tagTemplate => {
				const items = [];

				tagTemplate.items.forEach((item) => {
					const clip = this.clipsData.find(clip => {
						return clip.id === item.event_tag?.id || clip.tagTemplateItem?.id === item.id;
					});
					if (clip) {
						// Remove the clip from clipsData that are already in tagTemplates
						clips = clips.filter(clip => {
							if (item.event_tag) {
								return clip.id !== item.event_tag.id;
							}
							return clip.tagTemplateItem?.id !== item.id;
						});
						// Add the clip to the items list
						items.push({
							clip,
							isTagTemplate: false,
						});
					} else {
						items.push({
							...item,
							isTagTemplate: true,
						});
					}
				});

				// Add the tagTemplate section with its items
				sections.push({
					sectionTitle: tagTemplate.translatedArenaName,
					items:        this.searchKeyword?.length
						? items.filter(item => {
							if (item.clip) {
								return true;
							}
							return item.tag_name &&
								(
									item.tag_name.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
									item.start_nr.toLowerCase().includes(this.searchKeyword.toLowerCase())
								);
						})
						: items,
				});
			});

			// Add the clips section
			sections.push({
				sectionTitle: this.transChoiceUcFirst('commons.clip', 2),
				items:        clips.map(clip => ({ clip, isTagTemplate: false })),
			});

			return sections.filter(section => section.items.length);
		},
		tagTemplatesAndClipsIsEmtpy() {
			return this.tagTemplatesAndClips.map(section => section.items.length).reduce((acc, val) => acc + val, 0) === 0;
		},
		activeFiltersComp() {
			return this.activeFilters;
		},
		shareClipModalTitle() {
			return this.shareLabels.SHARE_CLIP;
		},
		urlLabel() {
			return this.shareLabels.CLIP_URL;
		},
		searchKeyword: {
			get() {
				return this.searchKeyWordStore;
			},
			set() {

			},
		},
		emptyMsg() {
			return {
				title:       this.transUcfirst('analysis.clip_search.no_results_for', { keyword: this.searchKeyword }),
				description: this.transUcfirst('analysis.clip_search.no_results_text'),
			};
		},
	},
	watch: {
		eventTags() {
			this.updateFilteredList();
		},
	},
	created() {
		this.setExcludedTags(TAG_NAMES[TAG_TYPE_CLOCK]);
		this.isSearchActive = this.searchKeyword?.length > 0;
		this.updateFilteredList();

		window.addEventListener('resize', this.onWindowResize);
	},
	destroyed() {
		window.removeEventListener('resize', this.onWindowResize);
	},
	methods: {
		...mapActions({
			setExcludedTags: 'clipFilters/setExcludedTags',
			searchClips:     'clipFilters/searchClips',
			clearSearch:     'clipFilters/clearSearch',
			shareNative:     'device/shareNative',
		}),
		openInitialAccordion() {
			this.$nextTick(() => {
				// Open the Accordion which includes the latest tag
				let index = 0;
				const accordion = this.$refs.accordion;
				const latestTag = this.clipsData.reduce((acc, tag) => {
					if (!acc || tag?.timestamp > acc?.timestamp) {
						return tag;
					}
					return acc;
				}, null);
				if (this.isLive && latestTag) {
					index = this.tagTemplatesAndClips.findIndex(section => section.items.some(item => item.clip?.id === latestTag?.id));
				}

				if (accordion && !this.accordionOpened) {
					accordion.open(index);
					this.accordionOpened = true;
				}
			});
		},
		openAllAccordions() {
			const accordion = this.$refs.accordion;
			if (accordion) {
				accordion.openAll();
			}
		},
		onWindowResize() {
			// make the search bar visible in case the mobile keyboard pushes it under the app tabs
			const el = document.getElementById('actions-wrapper')?.querySelector('.container-tabs .tabs');
			if (!el) {
				return;
			}
			const cliplistEl = this.$refs.clipList?.$el;

			if (!cliplistEl) {
				return;
			}
			const searchListHeader = cliplistEl.querySelector('.list--header');
			const searchEl = this.$refs.clipList.$el.querySelector('input.search-input');

			if (el && searchListHeader) {
				const tabsBox = el.getBoundingClientRect();
				const searchBox = searchListHeader.getBoundingClientRect();

				const isActive = document.activeElement === searchEl;

				if (searchBox.y < tabsBox.height + tabsBox.y && isActive) {
					document.scrollingElement.scrollTop = 180;
				}
			}
		},
		updateClips(clips) {
			this.clipsData = [...clips].filter(clip => !clip.isOutsideVideo);
		},
		updateFilteredList() {
			this.updateClips(this.$store.getters['clipFilters/filteredClips']);
			this.openInitialAccordion();
		},
		handlePlay(clip) {
			if (clip) {
				this.itemPlayingId = clip.id;
				playerBus.seekTag(clip);
				playerBus.play();
			}
		},
		handleShare(clip) {
			this.openShareModal(clip);
		},
		openShareModal(clip, onClose = () => {}) {
			this.shareTag = clip;
			this.shareUrl = clip.url;
			this.shareLinks = clip.shareLinks;
			if (clip.tag && clip.tag.name === 'goal') {
				const teamNameGoal = clip.team ? (clip.team.club ? clip.team.club.code : clip.team.name) : '';
				const teamHome = this.event.teamHome.club ? this.event.teamHome.club.code : this.event.teamHome.name;
				const teamGuest = this.event.teamGuest.club ? this.event.teamGuest.club.code : this.event.teamGuest.name;
				const scoreResult = this.clipsData
					.filter(el => {
						return el.tag.name === 'goal' && el.timestamp <= clip.timestamp;
					})
					.reduce(
						(acc, el) => {
							const teamCode = el.team ? (el.team.club ? el.team.club.code : el.team.name) : '';
							if (!acc[teamCode]) {
								acc[teamCode] = 1;
							} else {
								acc[teamCode]++;
							}
							return acc;
						},
						{
							[teamHome]:  0,
							[teamGuest]: 0,
						}
					);
				if (Object.keys(scoreResult).length !== 2) {
					console.log('Cannot calculate score...');
					this.goalScore = '';
				} else {
					this.goalScore = `(${Object.keys(scoreResult)
						.map(key => scoreResult[key])
						.join(':')}) ${teamNameGoal}`;
				}
			} else {
				this.goalScore = '';
			}
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url: this.shareUrl,
				});
			} else {
				this.isShareModalOpen = true;
			}

			this.onModalClose = onClose;
		},
		closeShareModal() {
			this.isShareModalOpen = false;
			if (this.onModalClose) {
				this.onModalClose();
				this.onModalClose = null;
			}
		},
		handleTabChange(tab) {
			this.setActiveTab(tab);
		},
		closeOrClearSearch() {
			if (this.searchKeyword === null) {
				this.isSearchActive = false;
			}
			this.clearSearch();
			this.updateFilteredList();
		},
		onSearchInput(value) {
			this.searchClips(value);
			this.updateFilteredList();
			this.openAllAccordions();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts.scss';

.clips-wrapper {
	.title-wrapper {
		h2 {
			@include text-md-2;
		}
	}
}

.search {
	position: absolute;
	display: flex;
	align-items: center;
	width: 100%;
	height: 100%;
}

.tag-section-title-w {
	margin: 16px 0px 16px 8px;
}

.tag-template-list__items {
	overflow-y: auto;
}
</style>
